import { api } from '@/api/endpoints';
import { mediaQueueRepo } from '@/data/repositories/mediaQueueRepo';
import { logQueueRepo } from '@/data/repositories/logQueueRepo';
import { connectivity } from '@/services/connectivity';
import { gpsService } from '@/services/gps';
import { repairMediaFieldName, type MediaKind, type MediaQueueItem } from '@/types/domain';
import { stampUploadFailure, stampUploadSuccess } from '@/lib/config';
import { createLogger } from '@/lib/logger';

const log = createLogger('mediaQueue');

let flushInflight: Promise<void> | null = null;

/**
 * The bytes to upload for a queued item: the canonical ArrayBuffer, or a legacy
 * Blob row read back into one. Returns null if a legacy Blob can no longer be
 * read (iOS evicted its backing store).
 */
async function resolveBytes(item: MediaQueueItem): Promise<ArrayBuffer | null> {
  if (item.bytes && item.bytes.byteLength > 0) return item.bytes;
  if (item.blob) {
    try {
      return await item.blob.arrayBuffer();
    } catch (err) {
      log.warn('legacy media blob unreadable', item.id, err);
      return null;
    }
  }
  return item.bytes ?? null;
}

interface EnqueueArgs {
  kind: MediaKind;
  assignmentId: string;
  /** The log entry (client id) this media is attached to. */
  logClientId: string;
  fieldName: string;
  blob: Blob;
  filename: string;
  mimeType: string;
  tag?: string | null;
  driverNumber?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export const mediaQueueService = {
  async enqueue(args: EnqueueArgs): Promise<MediaQueueItem> {
    const fix =
      args.latitude != null && args.longitude != null
        ? null
        : gpsService.peekFix();
    // Persist the payload as an ArrayBuffer, not a Blob: iOS/WebKit can drop a
    // Blob's backing bytes from IndexedDB across an app relaunch, which made
    // later re-sends upload empty files (server 400). ArrayBuffers survive.
    const { blob, ...rest } = args;
    const bytes = await blob.arrayBuffer();
    const row = await mediaQueueRepo.enqueue({
      ...rest,
      bytes,
      latitude: args.latitude ?? fix?.latitude ?? null,
      longitude: args.longitude ?? fix?.longitude ?? null,
      capturedAt: new Date().toISOString(),
    });
    void this.flush();
    return row;
  },

  /**
   * Two-step upload: a media item can only be sent once its parent log has
   * been accepted by the server (so we know the FileMaker record id). Items
   * whose log hasn't synced yet are skipped and retried next cycle.
   */
  async flush(): Promise<void> {
    if (flushInflight) return flushInflight;
    if (!connectivity.online) return;

    flushInflight = (async () => {
      try {
        const pending = await mediaQueueRepo.pending(5);
        for (const item of pending) {
          let fmRecordId = item.fmRecordId ?? undefined;
          if (!fmRecordId) {
            fmRecordId = await logQueueRepo.getServerRecordId(item.logClientId);
            if (!fmRecordId) {
              // Parent log not yet acknowledged; leave pending for next cycle.
              continue;
            }
            await mediaQueueRepo.resolveFmRecordId(item.id, fmRecordId);
          }
          // Rebuild a fresh Blob from the stored bytes. Fall back to a legacy
          // `blob` row (pre-ArrayBuffer captures); if neither yields any bytes
          // the payload was evicted by the OS, so fail with a clear message
          // instead of posting an empty file that 400s forever.
          const bytes = await resolveBytes(item);
          if (!bytes || bytes.byteLength === 0) {
            await mediaQueueRepo.quarantine(
              item.id,
              'Image data was cleared by the device before upload. Please re-capture this photo.',
            );
            continue;
          }
          // Correct any container field name an earlier build mis-generated
          // (dev_f010/dev_f011) so items queued before the fix still upload.
          const fieldName = repairMediaFieldName(item.fieldName);
          if (fieldName !== item.fieldName) {
            await mediaQueueRepo.setFieldName(item.id, fieldName);
          }
          await mediaQueueRepo.markInflight(item.id);
          const uploadBlob = new Blob([bytes], { type: item.mimeType });
          try {
            await api.uploadMedia(fmRecordId, fieldName, uploadBlob, item.filename, {
              latitude: item.latitude,
              longitude: item.longitude,
            });
            await mediaQueueRepo.markUploaded(item.id);
            stampUploadSuccess();
          } catch (err) {
            log.warn('media upload failed', item.id, err);
            const error = (err as Error).message ?? String(err);
            stampUploadFailure(error);
            await mediaQueueRepo.markFailed(item.id, error);
          }
        }
      } finally {
        flushInflight = null;
      }
    })();

    return flushInflight;
  },

  /**
   * Re-queue quarantined media whose payload is still recoverable (daily
   * automatic recovery after a server outage). Items quarantined because the
   * OS evicted their bytes are left alone: retrying those would only upload
   * an empty file, and the driver has already been told to re-capture.
   * Returns how many items were re-queued.
   */
  async retryQuarantined(): Promise<number> {
    const quarantined = await mediaQueueRepo.getQuarantined();
    let requeued = 0;
    for (const item of quarantined) {
      // Inspect the payload via a fresh primary-key read (index-query results
      // can surface buffers with an unreliable byteLength) and require
      // POSITIVE evidence of bytes before re-queueing: an item whose payload
      // the OS evicted must stay quarantined.
      const fresh = (await mediaQueueRepo.get(item.id)) ?? item;
      const bytes = await resolveBytes(fresh);
      if (!bytes || !(bytes.byteLength > 0)) continue;
      await mediaQueueRepo.requeueForResend(item.id);
      requeued++;
    }
    return requeued;
  },

  /**
   * End-to-end POD acknowledgment: verify each upload against the
   * MOBILE_LOG_MEDIA record it targeted (the *_PRESENT calc flags live on
   * that layout). The earlier assignment-level check produced permanent
   * false "stale POD" alarms for signatures: the TMS never flips signature
   * flags on the Assignment record, only on the media record itself.
   * Bounded to 25 records per cycle; the remainder verifies on later syncs.
   */
  async verifyPods(): Promise<void> {
    if (!connectivity.online) return;
    // First resolve anything past the TMS's 10-day media retention window:
    // those records are wiped server-side and can never confirm.
    await mediaQueueRepo.resolveExpiredVerificationWindow();
    const unverified = await mediaQueueRepo.getUnverified();
    if (!unverified.length) return;

    const byRecord = new Map<string, MediaQueueItem[]>();
    for (const item of unverified) {
      if (!item.fmRecordId) continue;
      const list = byRecord.get(item.fmRecordId) ?? [];
      list.push(item);
      byRecord.set(item.fmRecordId, list);
    }
    const entries = [...byRecord.entries()].slice(0, 25);
    if (!entries.length) return;

    try {
      const statuses = await api.podMediaStatus(
        entries.map(([recordId, items]) => ({
          recordId,
          fields: [...new Set(items.map((i) => i.fieldName))],
        })),
      );
      const byId = new Map(statuses.map((s) => [s.recordId, s] as const));
      for (const [recordId, items] of entries) {
        const status = byId.get(recordId);
        if (!status) continue;
        for (const item of items) {
          if (status.present[item.fieldName]) {
            await mediaQueueRepo.markVerified(item.id);
          }
        }
      }
    } catch (err) {
      log.debug('pod verification failed', err);
    }
  },

  async purgeExpired(): Promise<number> {
    return mediaQueueRepo.purgeExpired();
  },
};
